import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import type { BadgeCode, CollectibleBadge } from './badgeApi';
import { setBadgeCollectionConsent, setBadgeSelection } from './badgeApi';
import './BadgeCollection.css';

interface BadgeCollectionProps {
  courseId: string;
  badgeCollectionConsented: boolean;
  badges: CollectibleBadge[];
  selectedBadgeCode: BadgeCode | null;
  onProgressRefresh: () => void | Promise<void>;
}

function badgeTranslationKey(
  badgeCode: string,
  property: 'name' | 'description' | 'altText',
): string {
  return `badges.catalog.${badgeCode}.${property}`;
}

function BadgeCard({
  badge,
  selected,
  disabled,
  canSelect,
  onSelect,
}: {
  badge: CollectibleBadge;
  selected: boolean;
  disabled: boolean;
  canSelect: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const acquired = badge.state === 'ACQUIRED';
  const name = t(badgeTranslationKey(badge.badgeCode, 'name'), { defaultValue: badge.name });
  const description = t(badgeTranslationKey(badge.badgeCode, 'description'), {
    defaultValue: badge.description,
  });
  const accessibleName = acquired
    ? t(badgeTranslationKey(badge.badgeCode, 'altText'), { defaultValue: badge.altText })
    : t('badges.lockedImageAlt', { name });

  return (
    <li className={`badge-collection__card${acquired ? ' badge-collection__card--acquired' : ''}`}>
      <div className="badge-collection__image-wrap">
        {imageFailed ? (
          <span className="badge-collection__image-fallback">{name}</span>
        ) : (
          <img
            src={badge.imageUrl}
            alt={accessibleName}
            onError={() => setImageFailed(true)}
            className="badge-collection__image"
          />
        )}
      </div>
      <BadgeCardDetails
        badge={badge}
        name={name}
        description={description}
        acquired={acquired}
        selected={selected}
        disabled={disabled}
        canSelect={canSelect}
        onSelect={onSelect}
      />
    </li>
  );
}

function BadgeCardDetails({
  badge,
  name,
  description,
  acquired,
  selected,
  disabled,
  canSelect,
  onSelect,
}: {
  badge: CollectibleBadge;
  name: string;
  description: string;
  acquired: boolean;
  selected: boolean;
  disabled: boolean;
  canSelect: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="badge-collection__details">
      <h3>{name}</h3>
      <p>{description}</p>
      <p className="badge-collection__state">
        {acquired ? t('badges.acquired') : t('badges.locked')}
      </p>
      {acquired ? <BadgeEarnedDate earnedAt={badge.earnedAt} /> : <BadgeUnlockHint badge={badge} />}
      {acquired && canSelect ? (
        <BadgeSelectionButton
          name={name}
          selected={selected}
          disabled={disabled}
          onSelect={onSelect}
        />
      ) : null}
    </div>
  );
}

function BadgeEarnedDate({ earnedAt }: { earnedAt: string | null }) {
  const { t, i18n } = useTranslation();
  if (!earnedAt) {
    return null;
  }

  const date = new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' }).format(
    new Date(earnedAt),
  );
  return <p>{t('badges.earnedAt', { date })}</p>;
}

function BadgeUnlockHint({ badge }: { badge: CollectibleBadge }) {
  const { t } = useTranslation();
  if (!badge.triggerExercise) {
    return <p>{t('badges.noCondition')}</p>;
  }

  return (
    <p>
      {t('badges.unlockBy')}{' '}
      <Link to={`/exercise/${badge.triggerExercise.id}`}>{badge.triggerExercise.title}</Link>
    </p>
  );
}

function BadgeSelectionButton({
  name,
  selected,
  disabled,
  onSelect,
}: {
  name: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button type="button" disabled={disabled} onClick={onSelect} aria-pressed={selected}>
      {selected ? t('badges.clearSelection') : t('badges.selectForLeaderboard', { name })}
    </button>
  );
}

function BadgeCollection({
  courseId,
  badgeCollectionConsented,
  badges,
  selectedBadgeCode,
  onProgressRefresh,
}: BadgeCollectionProps) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<'consent' | 'withdrawal' | string | null>(null);
  const [consentDeclined, setConsentDeclined] = useState(false);

  async function saveConsent(consented: boolean) {
    setBusyAction(consented ? 'consent' : 'withdrawal');
    setErrorMessage(null);
    try {
      await setBadgeCollectionConsent(consented);
      await onProgressRefresh();
      setConsentDeclined(false);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('badges.saveFailed'));
    } finally {
      setBusyAction(null);
    }
  }

  async function chooseBadge(badgeCode: BadgeCode) {
    const nextSelection = selectedBadgeCode === badgeCode ? null : badgeCode;
    setBusyAction(badgeCode);
    setErrorMessage(null);
    try {
      await setBadgeSelection(courseId, nextSelection);
      await onProgressRefresh();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('badges.saveFailed'));
    } finally {
      setBusyAction(null);
    }
  }

  const displayedBadges = badges.slice(0, 6);

  return (
    <section className="badge-collection" aria-labelledby="collectible-badges-heading">
      <header className="badge-collection__header">
        <div>
          <h2 id="collectible-badges-heading">{t('badges.collectionTitle')}</h2>
          <p>{t('badges.collectionDescription')}</p>
        </div>
      </header>

      {!badgeCollectionConsented ? (
        <div className="badge-collection__consent" aria-labelledby="badge-consent-heading">
          <h3 id="badge-consent-heading">{t('badges.consentTitle')}</h3>
          <p>{t('badges.consentDisclosure')}</p>
          {!consentDeclined ? (
            <div className="badge-collection__actions">
              <button
                type="button"
                disabled={busyAction !== null}
                onClick={() => void saveConsent(true)}
              >
                {busyAction === 'consent' ? t('badges.saving') : t('badges.acceptConsent')}
              </button>
              <button type="button" onClick={() => setConsentDeclined(true)}>
                {t('badges.declineConsent')}
              </button>
            </div>
          ) : (
            <p role="status">{t('badges.consentDeclined')}</p>
          )}
        </div>
      ) : (
        <>
          <div className="badge-collection__public-disclosure">
            <p>{t('badges.publicDisclosure')}</p>
            <p>{t('badges.selectionDefault')}</p>
          </div>
          <p className="badge-collection__selection-status" aria-live="polite">
            {selectedBadgeCode ? t('badges.selectionActive') : t('badges.selectionNone')}
          </p>
        </>
      )}

      {errorMessage && <p role="alert">{errorMessage}</p>}

      <ul className="badge-collection__grid">
        {displayedBadges.map((badge) => (
          <BadgeCard
            key={badge.badgeCode}
            badge={badge}
            selected={selectedBadgeCode === badge.badgeCode}
            disabled={busyAction !== null}
            canSelect={badgeCollectionConsented}
            onSelect={() => void chooseBadge(badge.badgeCode)}
          />
        ))}
      </ul>

      {badgeCollectionConsented ? (
        <div className="badge-collection__withdrawal">
          <p>{t('badges.withdrawalWarning')}</p>
          <button
            type="button"
            className="badge-collection__withdraw"
            disabled={busyAction !== null}
            onClick={() => void saveConsent(false)}
          >
            {busyAction === 'withdrawal' ? t('badges.saving') : t('badges.withdrawConsent')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

export default BadgeCollection;
